import semver from './semver'
import type {
  Product,
  ComplianceOverridesFunctions,
} from '@/types/products.interfaces'
import {
  Override,
  ShopOverridesByClass,
  ProductCompatibilityTag,
  PhpCompatibilityInfo,
} from '@/types/overrides.interfaces'

/**
 * Check if overrides information is available from CDC props
 * @param xprops - CDC props containing potential override data
 * @returns true if override data is available
 */
export function checkOverridesAvailability(xprops: any): boolean {
  return xprops?.context?.overrides_on_shop !== undefined
}

/**
 * Create a standardized override key from className and propertyName
 * @param className - The class name
 * @param propertyName - The property name
 * @returns A standardized key for the override
 */
export function createOverrideKey(
  className: string,
  propertyName: string,
): string {
  return `${className}__${propertyName}`
}

/**
 * Extract and parse shop overrides data from the cdc props
 * @param xprops - The cdc props
 * @returns Shop overrides data in 3 different formats: by class, as a flat dictionary, and as a list
 */
export function parseShopOverrides(xprops: any) {
  const shopOverridesByClass: ShopOverridesByClass = xprops?.context
    ?.overrides_on_shop as ShopOverridesByClass

  if (!shopOverridesByClass) {
    return {
      shopOverridesByClass: undefined,
      shopOverrides: {},
      shopOverridesMap: new Map(),
      shopOverridesList: [],
    }
  }

  const shopOverrides: Record<string, Override> = {}
  const shopOverridesMap = new Map<string, string>()
  for (const classData of Object.values(shopOverridesByClass)) {
    for (const key of ['methods', 'properties', 'constants'] as const) {
      for (const [name, value] of Object.entries(classData[key])) {
        const overrideKey = createOverrideKey(classData.class.name, name)
        shopOverridesMap.set(overrideKey, value.module)
        shopOverrides[overrideKey] = value
      }
    }
  }

  const shopOverridesList: string[] = Object.keys(shopOverrides)

  return {
    shopOverridesByClass,
    shopOverrides,
    shopOverridesMap,
    shopOverridesList,
  }
}

/**
 * Parse the Shop PS version to have the same format as the product PS version
 * @param psVersion - The PS version
 * @param cache - Optional cache Map to store results for performance
 * @returns The parsed PS version
 */
export function parsePsVersion(psVersion: string): string {
  const version = semver.normalizeVersion(psVersion)

  return semver.lower(version, '8')
    ? semver.getPatchVersion(version)
    : semver.getMinorVersion(version)
}

/**
 * Get PHP compatibility information for a product
 *
 * @param product - The product to check
 * @param shopVersion - The PrestaShop version (e.g., "1.7.8", "8.0")
 * @param shopPhpVersion - The current PHP version of the shop
 * @returns PHP compatibility information including recommended version and compatibility status
 */
export function getProductPhpCompatibilityInfo(
  product: Product,
  shopVersion: string,
  shopPhpVersion: string,
): PhpCompatibilityInfo {
  const compatibility = product.compliance?.compatibility

  // Get recommended PHP version (verified first, fallback to default max)
  const recommended =
    compatibility?.versions?.[shopVersion] ||
    compatibility?.default_versions?.[shopVersion]?.max

  // Check if we use the fallback version
  const useFallbackVersion = !compatibility?.versions?.[shopVersion]

  // isVerified is true only if the shop version exists in compliance tested versions
  const isVerified = useFallbackVersion
    ? false
    : compatibility?.compliance_checked ?? false

  // Get the supported PHP version range for this PrestaShop version
  const supportedRange = compatibility?.default_versions?.[shopVersion]

  // Check if current PHP version is greater or equal to the minimum supported version
  const isCompatible = supportedRange
    ? semver.greaterOrEqual(shopPhpVersion, supportedRange.min)
    : false

  // isCompliance checks if the product is officially verified and PHP version meets requirements
  const isCompliance =
    recommended !== undefined &&
    isVerified &&
    semver.greaterOrEqual(shopPhpVersion, recommended)

  return {
    shopPhpVersion,
    recommended,
    isVerified,
    supportedRange,
    isCompliance,
    isCompatible,
  }
}

/**
 * Check if the product has common overrides with the shop overrides
 * @param product - The product to check
 * @param overridesList - The shop overrides list
 * @returns True if the product has common overrides with the overrides list, false otherwise
 */
export function productHasCommonOverrides(
  product: Product,
  shopOverridesMap: Map<string, string>,
): boolean {
  if (!product.compliance?.overrides?.classes) return false

  return product.compliance.overrides.classes.some((psClass) => {
    return Object.keys(psClass.functions).some((name) => {
      const overrideKey = createOverrideKey(psClass.class, name)
      if (shopOverridesMap.has(overrideKey)) {
        return (
          shopOverridesMap.get(overrideKey) !==
          product.technical_info.technical_name
        )
      }
    })
  })
}

/**
 * Calculate the compatibility tag for a product based on PHP compatibility and override conflicts
 *
 * @param product - The product to check for compatibility
 * @param shopOverridesMap - Map of shop overrides to check for conflicts
 * @param shopPsVersion - The shop's PrestaShop version
 * @param shopPhpVersion - The shop's PHP version
 * @returns The compatibility tag indicating installation complexity level
 */
export function calculateProductCompatibilityTag(
  product: Product,
  shopOverridesMap: Map<string, string>,
  shopPsVersion: string,
  shopPhpVersion: string,
  overridesSupported: boolean,
): ProductCompatibilityTag {
  const phpCompatibility = getProductPhpCompatibilityInfo(
    product,
    shopPsVersion,
    shopPhpVersion,
  )

  // If neither officially tested nor within supported range → requires technical skills
  if (!phpCompatibility.isCompliance && !phpCompatibility.isCompatible) {
    return ProductCompatibilityTag.TECHNICAL_SKILLS_REQUIRED
  }

  // If cdc provide overrides context and product has conflicting overrides → requires manual merge
  if (overridesSupported && productHasCommonOverrides(product, shopOverridesMap)) {
    return ProductCompatibilityTag.MERGING_FUNCTION_REQUIRED
  }

  // If cdc provide overrides context, product is officially tested and compatible → perfect match
  if (overridesSupported && phpCompatibility.isCompliance) {
    return ProductCompatibilityTag.PERFECT_MATCH
  }

  // Otherwise, compatible but not officially tested → general compatibility
  return ProductCompatibilityTag.COMPATIBLE
}

export function enhanceProductOverridesWithShopOverrides(
  product: Product,
  shopOverrides: Record<string, Override>,
): Product {
  // Early return if no classes to process
  if (!product.compliance?.overrides?.classes?.length) {
    return {
      ...product,
      compliance: {
        ...product.compliance,
        compatibility: product.compliance?.compatibility || {
          compliance_checked: false,
          versions: {},
          default_versions: {},
        },
        overrides: {
          compliance_checked:
            product.compliance?.overrides?.compliance_checked || false,
          classes: product.compliance?.overrides?.classes || [],
          summary: {
            overridenFunctionsCount:
              product.compliance?.overrides?.summary?.overridenFunctionsCount ||
              0,
            recurringOverridesCount:
              product.compliance?.overrides?.summary?.recurringOverridesCount ||
              0,
            topSellerOverrideChecked:
              product.compliance?.overrides?.summary
                ?.topSellerOverrideChecked || false,
            overrideWithShopCount: 0,
          },
        },
      },
    }
  }

  let overrideWithShopCount = 0
  const productTechnicalName = product.technical_info.technical_name

  const enhancedClasses = product.compliance.overrides.classes.map(
    (psClass) => {
      const enhancedFunctions: Record<string, ComplianceOverridesFunctions> = {}

      for (const [propertyName, value] of Object.entries(psClass.functions)) {
        const overrideKey = createOverrideKey(psClass.class, propertyName)
        const shopOverride = shopOverrides[overrideKey]

        const moduleInConflict =
          shopOverride && shopOverride.module !== productTechnicalName
            ? shopOverride.module
            : null

        if (moduleInConflict) overrideWithShopCount++

        enhancedFunctions[propertyName] = {
          ...value,
          override_module: moduleInConflict,
        }
      }

      return { ...psClass, functions: enhancedFunctions }
    },
  )

  return {
    ...product,
    compliance: {
      ...product.compliance,
      overrides: {
        ...product.compliance.overrides,
        classes: enhancedClasses,
        summary: {
          overridenFunctionsCount:
            product.compliance.overrides.summary?.overridenFunctionsCount || 0,
          recurringOverridesCount:
            product.compliance.overrides.summary?.recurringOverridesCount || 0,
          topSellerOverrideChecked:
            product.compliance.overrides.summary?.topSellerOverrideChecked ||
            false,
          overrideWithShopCount,
        },
      },
    },
  }
}

/** * Determine if overrides are available for a given MBO version
 * @param mboVersion - The MBO version to check
 * @returns true if overrides are available for the MBO version
 */
export function overrideAvailableForMboVersion(mboVersion: string): boolean {
  return (
    semver.inRange(mboVersion, '3.3.1', '3.99.00') ||
    semver.inRange(mboVersion, '4.13.4', '4.99.00') ||
    semver.greaterOrEqual(mboVersion, '5.1.0')
  )
}

/** * Determine if a given MBO version can be upgraded to an overrides compatible version
 * @param mboVersion - The MBO version to check
 * @returns true if the MBO version can be upgraded to an overrides compatible version
 */
export function inVersionUpgradbleToOverride(mboVersion: string): boolean {
  return semver.greaterOrEqual(mboVersion, '3.0.0')
}
